import { Accordion, Flex, Text } from '@chakra-ui/react';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  coverageTypes,
  planPaymentMode,
  backgroundTypeEnum,
} from 'common/utils/enums';
import PlanInfoCard from 'components/plans/plan-info-card';
import { selectUser } from 'redux/features/auth/authSlice';
import PaymentSummaryCard from 'components/plans/payment-summary-card';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import { PostPurchaseCoverageCard } from 'components/coverages/coverage-card';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import { InfoDetail } from 'components/shared/member-info-card';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import FormatAsCurrency from 'common/utils/formatAsCurrency';
import { leaseTypeEnum } from 'common/utils/enums';
import { ApplyFormLayout } from 'common/layouts/apply-form';

const PlanInReview = ({ isGettingPlan, planData }) => {
  const { trackPageLoad, trackLink } = useUserTracker();
  useEffect(() => {
    trackPageLoad('PlanInReview');
  }, []);

  // SIDE EFFECTS
  // Retrieve user details
  const userDetails = useSelector(selectUser);

  // HOOKS
  const navigate = useNavigate();

  // DATA INITIALIZATION
  const cancelAnytime = planData?.coverage?.cancelAnytime;
  const leaveAnytime = planData?.coverage?.leaveAnytime;

  const hasCancelAnytime = !!cancelAnytime?.payment?.stripe?.id;
  const hasLeaveAnytimeOnetime = !!leaveAnytime?.payment?.onetime?.stripe?.id;
  const hasLeaveAnytimeSubscription =
    !!leaveAnytime?.payment?.subscription?.stripe?.id;
  const hasLeaveAnytimeSplit =
    !!leaveAnytime?.payment?.split?.subscription?.stripe?.id ||
    !!leaveAnytime?.payment?.split?.onetime?.stripe?.id;

  const hasLeaveAnytime =
    hasLeaveAnytimeOnetime ||
    hasLeaveAnytimeSubscription ||
    hasLeaveAnytimeSplit;
  const hasCoverage = hasCancelAnytime || hasLeaveAnytime;

  const program = planData?.property?.owner?.program;
  const rates = planData?.property?.rates?.underwriting;
  const concessions = planData?.lease?.concessions?.repayable
    ? planData?.lease?.concessions?.discount || 0
    : 0;

  const isMonthToMonth =
    planData?.lease?.leaseType === leaseTypeEnum.MONTH_TO_MONTH_LEASE;

  const brand = planData?.property?.brand;
  const isDarkBg = brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;

  useEffect(() => {
    console.log(planData);
  }, [planData]);

  return (
    <div className='flex flex-col w-full h-full'>
      <ApplyTitle
        title='Review in Progress'
        subtitle='We’re reviewing your details and will update you shortly.'
        buttons={
          <ApplyButtons
            isValid={true}
            handleSubmit={() => {
              navigate('/dashboard/plans');
            }}
            onNextClick={() => {
              navigate('/dashboard/plans');
            }}
            disableBack
            nextTitle='Home'
          />
        }
      />

      {/* main content */}
      <ApplyContent>
        {planData?.property && (
          <ApplyFormLayout
            className={`p-6 rounded-lg border relative overflow-clip ${
              brand ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
            }`}
            style={brand?.bgColor ? { backgroundColor: brand.bgColor } : {}}
          >
            <Flex className='z-10 relative flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
              {brand && (
                <img
                  src={isDarkBg && brand?.logoAlt ? brand?.logo : brand?.logo}
                  alt={planData?.property?.name}
                  className='max-w-[140px] max-h-[50px] object-contain'
                />
              )}
              <Flex className='flex-col gap-1'>
                <Text
                  className={`font-medium text-lg ${
                    isDarkBg ? 'text-white' : 'text-black'
                  }`}
                >
                  ReLease Coverage at {planData?.property?.name}
                </Text>
                <Text
                  className={`text-base ${isDarkBg ? 'text-white opacity-60' : 'text-black opacity-60'}`}
                >
                  {planData?.property?.address?.streetAddress1},{' '}
                  {planData?.property?.address?.city},{' '}
                  {planData?.property?.address?.state}{' '}
                  {planData?.property?.address?.postalCode}
                </Text>
              </Flex>
            </Flex>
            {brand?.backgroundHeroImage && (
              <img
                src={brand?.backgroundHeroImage}
                alt={planData?.property?.name}
                className='absolute inset-0 opacity-15 z-0 object-cover'
              />
            )}
          </ApplyFormLayout>
        )}
        <Flex className='flex-col gap-4 w-full p-6 border border-gray-200 bg-gray-50 rounded-md'>
          <h2 className='text-lg font-medium'>
            Our team is reviewing your information
          </h2>
          <Flex className='flex-col gap-2'>
            <Text className='text-base text-gray-500'>
              We're reviewing the details you provided us - this will only take
              1-2 days.
            </Text>
            <Text className='text-base text-gray-500'>
              We'll notify you if we have any questions, and once your coverage
              has been confirmed and activated.
            </Text>
          </Flex>
        </Flex>

        {/* plan info card */}
        <Flex className='flex-col gap-4 w-full'>
          <PlanInfoCard plan={planData} member={userDetails} />
        </Flex>

        <PaymentSummaryCard
          isLoading={false}
          isComplete={true}
          plan={planData}
        />

        {/* plan coverages section */}
        {hasCoverage ? (
          <Flex className='flex-col gap-6'>
            {/* coverages heading */}
            <Flex className='flex-col gap-1 w-full items-left'>
              <h2 className='text-lg font-medium'>Your Coverage</h2>
              <Text className='text-base text-gray-500'>
                Your plan includes the following coverages:
              </Text>
            </Flex>

            {/* coverages */}
            {hasCancelAnytime && (
              <PostPurchaseCoverageCard
                coverageType={coverageTypes.CANCEL_ANYTIME}
              >
                <Accordion>
                  <InfoDetail
                    title='Coverage Period'
                    subtitle={`${formatDateToHumanDate(
                      cancelAnytime?.startDate
                    )} - ${formatDateToHumanDate(cancelAnytime?.endDate)}`}
                  >
                    <Text>
                      Your coverage period starts on{' '}
                      {formatDateToHumanDate(cancelAnytime?.startDate)} and ends
                      on {formatDateToHumanDate(cancelAnytime?.endDate)}. You
                      are eligible to cancel your lease during this
                      period and receive benefits.
                    </Text>
                  </InfoDetail>
                  <InfoDetail
                    title='Deductible'
                    subtitle={
                      <span>
                        {FormatAsCurrency(
                          cancelAnytime?.deductible || 0,
                          'USD'
                        )}
                        <span className='text-gray-500'>
                          {' '}
                          (due at time of claim)
                        </span>
                      </span>
                    }
                  >
                    <Text>
                      Your coverage includes a deductible in the amount of{' '}
                      {FormatAsCurrency(cancelAnytime?.deductible || 0, 'USD')}.
                      This amount must be paid at the time of a claim before
                      ReLease begins covering the rest of your early termination
                      fees.
                    </Text>
                  </InfoDetail>
                  <InfoDetail
                    title='Notice Period'
                    subtitle={
                      <span>
                        {cancelAnytime?.noticePeriod || 3} days{' '}
                        <span className='text-gray-500'>
                          (before move-out date)
                        </span>
                      </span>
                    }
                  >
                    <Text>
                      You must provide at least {cancelAnytime?.noticePeriod || 3} days notice before your move-out date. 
                      This means you need to submit your claim at least {cancelAnytime?.noticePeriod || 3} days before 
                      the earliest date you want to move out.
                    </Text>
                  </InfoDetail>
                  {concessions > 0 ? (
                    <InfoDetail
                      title='Non-Covered Expenses'
                      subtitle={FormatAsCurrency(concessions, 'USD')}
                    >
                      <Text>
                        ReLease does not cover the{' '}
                        {FormatAsCurrency(concessions, 'USD')} in concessions
                        you received from your landlord as part of this lease
                        agreement.
                      </Text>

                      <Text>
                        ReLease does not cover any concessions or discounts you
                        receive from your landlord. Any non-covered expenses
                        will need to be paid out of pocket prior to receiving
                        benefits. ReLease does not cover or change any terms
                        related to cleaning fees, pet fees, or other fees that
                        are not part of your lease agreement.
                      </Text>
                    </InfoDetail>
                  ) : null}
                </Accordion>
              </PostPurchaseCoverageCard>
            )}
            {hasLeaveAnytime && (
              <PostPurchaseCoverageCard
                coverageType={coverageTypes.LEAVE_ANYTIME}
              >
                <Accordion>
                <InfoDetail
                  title='Coverage Period'
                  subtitle={
                    isMonthToMonth
                      ? `Month-to-month from ${formatDateToHumanDate(
                          leaveAnytime?.startDate
                        )}`
                      : `${formatDateToHumanDate(
                          leaveAnytime?.startDate
                        )} - ${formatDateToHumanDate(leaveAnytime?.endDate)}`
                  }
                >
                  {isMonthToMonth ? (
                    <Text>
                      Your coverage period starts on{' '}
                      {formatDateToHumanDate(leaveAnytime?.startDate)} and
                      renews each month until the end of your lease agreement.
                      You are eligible to leave your lease during
                      this period and receive benefits.
                    </Text>
                  ) : (
                    <Text>
                      Your coverage period starts on{' '}
                      {formatDateToHumanDate(leaveAnytime?.startDate)} and ends
                      on {formatDateToHumanDate(leaveAnytime?.endDate)}. You are
                      eligible to leave your lease during this
                      period and receive benefits.
                    </Text>
                  )}
                </InfoDetail>
                <InfoDetail
                  title='Deductible'
                  subtitle={
                    <span>
                      {FormatAsCurrency(leaveAnytime?.deductible || 0, 'USD')}
                      <span className='text-gray-500'>
                        {' '}
                        (due at time of claim)
                      </span>
                    </span>
                  }
                >
                  <Text>
                    Your coverage includes a deductible in the amount of{' '}
                    {FormatAsCurrency(leaveAnytime?.deductible || 0, 'USD')}.
                    This amount must be paid at the time of a claim before
                    ReLease begins covering the rest of your early termination
                    fees.
                  </Text>
                </InfoDetail>
                <InfoDetail
                  title='Notice Period'
                  subtitle={
                    <span>
                      {leaveAnytime?.noticePeriod || 3} days{' '}
                      <span className='text-gray-500'>
                        (before move-out date)
                      </span>
                    </span>
                  }
                >
                  <Text>
                    You must provide at least {leaveAnytime?.noticePeriod || 3} days notice before your move-out date. 
                    This means you need to submit your claim at least {leaveAnytime?.noticePeriod || 3} days before 
                    the earliest date you want to move out.
                  </Text>
                </InfoDetail>
                {concessions > 0 ? (
                  <InfoDetail
                    title='Non-Covered Expenses'
                    subtitle={FormatAsCurrency(concessions, 'USD')}
                  >
                    <Text>
                      ReLease does not cover the{' '}
                      {FormatAsCurrency(concessions, 'USD')} in concessions you
                      received from your landlord as part of this lease
                      agreement.
                    </Text>

                    <Text>
                      ReLease does not cover any concessions or discounts you
                      receive from your landlord. Any non-covered expenses will
                      need to be paid out of pocket prior to receiving benefits.
                      ReLease does not cover or change any terms related to
                      cleaning fees, pet fees, or other fees that are not part
                      of your lease agreement.
                    </Text>
                  </InfoDetail>
                ) : null}
                </Accordion>
              </PostPurchaseCoverageCard>
            )}
          </Flex>
        ) : null}
      </ApplyContent>
    </div>
  );
};

export default PlanInReview;
